import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { skipToken, useQuery } from '@tanstack/react-query'
import { useAccount, usePublicClient } from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config'
import { AuthKeeperAbi } from '@repo/common/queries/blockchain/abis/AuthKeeper'
import {
  type KYCLevel,
  KYCLevels,
} from '@repo/common/queries/blockchain/abis/AuthKeeperLevels'

import { FissionTokenAbi } from '../blockchain/abis/FissionToken'
import { KYCComplianceFacetAbi } from '../blockchain/abis/KYCComplianceFacet'
import { KYCComplianceModuleAbi } from '../blockchain/abis/KYCComplianceModule'
import { RestrictionTypes } from '../blockchain/abis/KYCComplianceModuleRestrictions'
import {
  disableKeyWhenMissing,
  type DisableKeyWhenMissingFlag,
  reportEnableFailed,
} from '../helpers/utilities'
import {
  fund_symbols,
  type ProductSymbol,
  useGetTokenAddress,
} from '../products'

export { type KYCLevel }

export const keys = createQueryKeyStore({
  token_auth: {
    is_restricted: (variables: {
      symbol: ProductSymbol
      wallet_address: CryptoAddress | DisableKeyWhenMissingFlag
    }) => [variables],
    kyc_compliance_address: (variables: { symbol: ProductSymbol }) => [
      variables,
    ],
    auth_keeper_address: null,
    kyc_level: (variables: {
      wallet_address: CryptoAddress | DisableKeyWhenMissingFlag
    }) => [variables],
    sdd_settings: (variables: { symbol: ProductSymbol }) => [variables],
    sdd_purchase_stats: (variables: {
      symbol: ProductSymbol
      wallet_address: CryptoAddress | DisableKeyWhenMissingFlag
    }) => [variables],
  },
})

export function useGetKYCComplianceAddress({
  symbol,
}: {
  symbol: ProductSymbol
}) {
  const token_address = useGetTokenAddress({ symbol })
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = token_address != null && client != null

  reportEnableFailed({
    name: 'useGetKYCComplianceAddress',
    parts: {
      client,
      token_address,
    },
  })

  return useQuery({
    staleTime: Infinity,
    gcTime: Infinity,
    queryKey: keys.token_auth.kyc_compliance_address({ symbol }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await client.readContract({
            abi: KYCComplianceFacetAbi,
            address: token_address,
            functionName: 'getKYCModules',
          })
          return result == null ? result : result[0]
        },
  })
}

export function useGetAuthKeeperAddress() {
  const { data: kyc_compliance_address } = useGetKYCComplianceAddress({
    symbol: fund_symbols[0],
  })
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = kyc_compliance_address != null && client != null

  reportEnableFailed({
    name: 'useGetAuthKeeperAddress',
    parts: {
      client,
      kyc_compliance_address,
    },
  })

  return useQuery({
    staleTime: Infinity,
    gcTime: Infinity,
    queryKey: keys.token_auth.auth_keeper_address.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () =>
          client.readContract({
            abi: KYCComplianceModuleAbi,
            address: kyc_compliance_address,
            functionName: 'getAuthKeeper',
          }),
  })
}

type is_restricted_data_type = {
  mint: boolean
  mint_for_sdd: boolean
  trade_fdex: boolean
  trade_other: boolean
}

export function useCheckIsRestricted({ symbol }: { symbol: ProductSymbol }) {
  const { data: kyc_compliance_address } = useGetKYCComplianceAddress({
    symbol,
  })
  const { address: wallet_address } = useAccount()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    kyc_compliance_address != null && wallet_address != null && client != null

  reportEnableFailed({
    name: 'useCheckIsRestricted',
    parts: {
      client,
      wallet_address,
      kyc_compliance_address,
    },
  })

  return useQuery<is_restricted_data_type>({
    staleTime: Infinity,
    queryKey: keys.token_auth.is_restricted({
      symbol,
      wallet_address: disableKeyWhenMissing(wallet_address),
    }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const results = await client.multicall({
            contracts: [
              {
                abi: KYCComplianceModuleAbi,
                address: kyc_compliance_address,
                functionName: 'isRestricted',
                args: [RestrictionTypes.indexOf('Mint'), wallet_address],
              },
              {
                abi: KYCComplianceModuleAbi,
                address: kyc_compliance_address,
                functionName: 'isRestricted',
                args: [RestrictionTypes.indexOf('MintForSDD'), wallet_address],
              },
              {
                abi: KYCComplianceModuleAbi,
                address: kyc_compliance_address,
                functionName: 'isRestricted',
                args: [RestrictionTypes.indexOf('TradeFDEX'), wallet_address],
              },
              {
                abi: KYCComplianceModuleAbi,
                address: kyc_compliance_address,
                functionName: 'isRestricted',
                args: [RestrictionTypes.indexOf('TradeOther'), wallet_address],
              },
            ],
          })

          return {
            mint: results[0].result == true,
            mint_for_sdd: results[1].result == true,
            trade_fdex: results[2].result == true,
            trade_other: results[3].result == true,
          }
        },
  })
}

type sdd_settings_type = {
  maxSingleDepositUSDCAmount: bigint
  maxSingleDayDepositUSDCAmount: bigint
  maxDurationDepositUSDCAmount: bigint
  maxSingleDayDeposits: number
  maxDurationDeposits: number
  durationDays: number
}

export function useGetSDDSettings({ symbol }: { symbol: ProductSymbol }) {
  const token_address = useGetTokenAddress({ symbol })
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = token_address != null && client != null

  reportEnableFailed({
    name: 'useGetSDDSettings',
    parts: {
      client,
      token_address,
    },
  })

  return useQuery<sdd_settings_type>({
    staleTime: Infinity,
    queryKey: keys.token_auth.sdd_settings({ symbol }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          return await client.readContract({
            abi: FissionTokenAbi,
            address: token_address,
            functionName: 'getSDDSettings',
            args: [],
          })
        },
  })
}

type sdd_purchase_stats_type = {
  deposits_today: number
  deposits_today_USDC_amount: bigint
  deposits_over_duration: bigint
  deposits_over_duration_USDC_amount: bigint
  todays_last_time: bigint | null
  durations_last_time: bigint | null
}

export function useGetSDDPurchaseStats({ symbol }: { symbol: ProductSymbol }) {
  const token_address = useGetTokenAddress({ symbol })
  const { address: wallet_address } = useAccount()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    token_address != null && wallet_address != null && client != null

  reportEnableFailed({
    name: 'useGetSDDPurchaseStats',
    parts: {
      client,
      token_address,
    },
  })

  return useQuery<sdd_purchase_stats_type>({
    queryKey: keys.token_auth.sdd_purchase_stats({
      symbol,
      wallet_address: disableKeyWhenMissing(wallet_address),
    }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const results = await client.readContract({
            abi: FissionTokenAbi,
            address: token_address,
            functionName: 'getSimpleKYCPurchaseStats',
            args: [wallet_address],
          })

          return {
            deposits_today: results[0],
            deposits_today_USDC_amount: results[1],
            deposits_over_duration: results[2],
            deposits_over_duration_USDC_amount: results[3],
            todays_last_time: results[2] == 0n ? null : results[4],
            durations_last_time: results[3] == 0n ? null : results[5],
          }
        },
  })
}

export function useCheckKYCLevel({
  refetch_until_level,
}: {
  refetch_until_level?: KYCLevel[]
} = {}) {
  const { data: auth_keeper_address } = useGetAuthKeeperAddress()
  const { address: wallet_address } = useAccount()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    auth_keeper_address != null && wallet_address != null && client != null

  reportEnableFailed({
    name: 'useCheckKYCLevel',
    parts: {
      client,
      wallet_address,
      auth_keeper_address,
    },
  })

  return useQuery<KYCLevel>({
    gcTime: Infinity,
    queryKey: keys.token_auth.kyc_level({
      wallet_address: disableKeyWhenMissing(wallet_address),
    }).queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await client.readContract({
            abi: AuthKeeperAbi,
            address: auth_keeper_address,
            functionName: 'getKYCLevel',
            args: [wallet_address],
          })

          return KYCLevels[result]
        },
    refetchInterval: (query) => {
      return refetch_until_level?.includes(query.state.data ?? 'none')
        ? 5000
        : false
    },
  })
}
